import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CommunicationModal } from '@edu-frontend/modules/src/features/CommunicationModal';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';
import {
    getUserProps,
    postBulkUserProps,
    getNewSubscriptions,
    postNewSubscriptions,
} from '@yandex-int/k-common/client/api';
import { getExperimentFlags, useIntl, useMe } from 'platform-components';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import { CommunicationViewProps } from '@edu-frontend/modules/src/entities/user/ui/teacher/CommunicationView/CommunicationView';

export function Communication() {
    const experimentFlags = useSelector(getExperimentFlags) as Record<string, unknown> | null;

    const [visible, setVisible] = useState(false);

    const myId = useMe('id');

    const isEnabled = experimentFlags?.enableNewProfile && experimentFlags?.enableCommNotificationModal;

    const { data, isSuccess } = useQuery(['getUserProps', myId], getUserProps, {
        enabled: myId !== undefined,
        refetchOnWindowFocus: true,
    });

    const { data: subscriptions, isSuccess: isSubscriptionsSuccess } = useQuery(
        ['getNewSubscriptions'],
        getNewSubscriptions,
        {
            enabled: isEnabled && !data?.communicationAgreementModalWindowShown?.value,
            refetchOnWindowFocus: true,
        }
    );

    useEffect(() => {
        if (isSuccess && isSubscriptionsSuccess && !data?.communicationAgreementModalWindowShown?.value) {
            setVisible(true);
        }
    }, [data, isSubscriptionsSuccess, isSuccess]);

    const [communications, setCommunication] = useState<CommunicationViewProps['communications']>({
        kryau: true,
        yateachers: true,
        yau_ege: true,
        yau_newprod: true,
        yautpromo: false,
    });

    useEffect(() => {
        if (subscriptions) {
            setCommunication(subscriptions);
        }
    }, [subscriptions]);

    const [bulkAddOrUpdateUserProps] = useMutation(async () => {
        setVisible(false);

        await postBulkUserProps({
            values: [
                {
                    name: 'communication_agreement_modal_window_shown',
                    value: true,
                },
            ] as const,
        });

        await postNewSubscriptions(communications);
    });

    const intl = useIntl();

    const getIntlText = useCallback(
        (id: string, values?: Record<string, string>) => intl.formatMessage({ id }, values),
        [intl]
    );

    const texts = useMemo<Parameters<typeof CommunicationModal>[0]['texts']>(
        () => ({
            communications: {
                kryau: getIntlText('communication.checkbox.kryau.title'),
                yateachers: getIntlText('communication.checkbox.Yateachers.title'),
                yau_ege: getIntlText('communication.checkbox.yau_ege.title'),
                yau_newprod: getIntlText('communication.checkbox.yau_newprod.title'),
                yautpromo: getIntlText('communication.checkbox.yautpromo.title'),
            },
            adTitle: getIntlText('communication.ad.title'),
            infoTitle: getIntlText('communication.info.title'),
            legalLink: getIntlText('communication.legal.link'),
            legalTitle: getIntlText('communication.legal.title'),
            applyButton: getIntlText('communication.modal.applyButton'),
            title: getIntlText('communication.modal.title'),
            caption: getIntlText('communication.modal.caption'),
        }),
        [getIntlText]
    );

    if (!isEnabled) {
        return null;
    }

    return (
        <ThemeProvider isRoot={false} theme={hrLight}>
            <CommunicationModal
                communications={communications}
                onApply={bulkAddOrUpdateUserProps}
                setCommunication={setCommunication}
                texts={texts}
                visible={visible}
            />
            ;
        </ThemeProvider>
    );
}
